import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { EmptyListContent } from '@euler/app/components/list/EmptyListContent';
import { InfiniteListFooter } from '@euler/app/components/list/InfiniteListFooter';
import { useServiceRecords } from '@euler/app/flows/record/functions/useServiceRecords';
import { ServiceRecordCell } from '@euler/app/flows/record/pages/detail/components/RecentServiceRecords';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { VehicleServiceRecord } from '@euler/model';
import { WithExtraVehicleModelProps } from '@euler/model/WithExtraVehicleModelProps';
import { useCallback, useEffect } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Record = WithExtraVehicleModelProps<VehicleServiceRecord>;

const keyExtractor = (record: VehicleServiceRecord) => record._id;

export const ServiceRecordsPage = wrapNavigatorScreen(
  ({
    licensePlateNo,
    vin,
    endDate,
    endInclusive,
  }: {
    licensePlateNo: string;
    vin: string;
    endDate?: string | Date;
    endInclusive?: boolean;
    title?: string;
  }) => {
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const navigation = useAppNavigation();

    const { data, status, fetch, fetchMore, fetchSinceLastError } =
      useServiceRecords({
        licensePlateNo,
        vin,
        endDate,
        endInclusive,
      });

    const onPress = useCallback(
      (record: VehicleServiceRecord) => {
        navigation.push('ServiceRecord', { id: record._id });
      },
      [navigation],
    );

    const renderItem: ListRenderItem<Record> = useCallback(
      ({ item, index }) => {
        return (
          <ServiceRecordCell
            record={item}
            onPress={onPress}
            css={`
              border-top-width: ${index === 0 ? 0 : 1}px;
            `}
          />
        );
      },
      [onPress],
    );

    const onEndReached = useCallback(() => {
      void fetchMore();
    }, [fetchMore]);

    useEffect(() => {
      void fetch();
    }, [fetch]);

    return (
      <FlatList
        data={data}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        onEndReachedThreshold={0.3}
        onEndReached={onEndReached}
        refreshControl={
          Platform.OS === 'web' ? undefined : (
            <RefreshControl
              refreshing={status.isRefreshing && status.loadedDataLength > 0}
              onRefresh={fetch}
            />
          )
        }
        ListEmptyComponent={
          <EmptyListContent
            loading={!data.length && status.isLoading}
            error={status.error}
            emptyText="无更多历史记录"
          />
        }
        ListFooterComponent={
          <InfiniteListFooter
            status={status}
            onRecoverFromError={fetchSinceLastError}
            emptyText={''}
          />
        }
        contentContainerStyle={{
          backgroundColor: '#fff',
          paddingVertical: rpx(10),
          paddingHorizontal: rpx(10),
          paddingBottom: rpx(15) + bottom,
          flexGrow: 1,
        }}
      />
    );
  },
  ({ route }) => ({
    title: route.params.title ?? '服务记录',
  }),
);
